import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Home, ArrowLeft, Search, MapPin, Bed, Bath, PoundSterling,
  Calendar, Loader2, Building2, ExternalLink, Eye
} from "lucide-react";
import { API } from "@/App";

const getBuyerAuth = () => {
  const token = localStorage.getItem("buyer_token");
  return { token };
};

export const PropertySearch = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const { token } = getBuyerAuth();
  
  // Filters
  const [filters, setFilters] = useState({
    min_price: "",
    max_price: "",
    min_bedrooms: "",
    area: ""
  });
  
  // Viewing request modal
  const [viewingModal, setViewingModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [viewingDate, setViewingDate] = useState("");
  const [viewingTime, setViewingTime] = useState("");
  const [viewingNotes, setViewingNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.min_price && filters.min_price !== "any") params.append("min_price", filters.min_price);
      if (filters.max_price && filters.max_price !== "any") params.append("max_price", filters.max_price);
      if (filters.min_bedrooms && filters.min_bedrooms !== "any") params.append("min_bedrooms", filters.min_bedrooms);
      if (filters.area) params.append("area", filters.area);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/buyer-portal/properties?${params.toString()}`);
      setProperties(response.data.properties || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProperties();
  };

  const openViewingModal = (property) => {
    if (!token) {
      toast.error("Please login to book a viewing");
      navigate("/buyer/login");
      return;
    }
    setSelectedProperty(property);
    setViewingDate("");
    setViewingTime("");
    setViewingNotes("");
    setViewingModal(true);
  };

  const submitViewingRequest = async () => {
    if (!viewingDate || !viewingTime) {
      toast.error("Please select a date and time");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/buyer-portal/viewings/request`,
        {
          property_id: selectedProperty.case_id,
          preferred_date: viewingDate,
          preferred_time: viewingTime,
          notes: viewingNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Viewing request submitted! We'll confirm shortly.");
      setViewingModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request viewing");
    } finally {
      setSubmitting(false);
    }
  };

  // Generate next 14 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      if (date.getDay() !== 0) { // Skip Sundays
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    return dates;
  };

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30"
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/buyer/dashboard")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-800">Property Search</h1>
                <p className="text-sm text-slate-500">{total} properties available</p>
              </div>
            </div>
            {!token ? (
              <Button onClick={() => navigate("/buyer/login")}>
                Sign In to Book Viewings
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/buyer/dashboard")}>
                My Dashboard
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[150px]">
                <Label>Location</Label>
                <Input
                  placeholder="Postcode or area..."
                  value={filters.area}
                  onChange={(e) => setFilters(prev => ({ ...prev, area: e.target.value }))}
                />
              </div>
              <div className="w-32">
                <Label>Min Price</Label>
                <Select
                  value={filters.min_price}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, min_price: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="100000">£100k</SelectItem>
                    <SelectItem value="150000">£150k</SelectItem>
                    <SelectItem value="200000">£200k</SelectItem>
                    <SelectItem value="250000">£250k</SelectItem>
                    <SelectItem value="300000">£300k</SelectItem>
                    <SelectItem value="350000">£350k</SelectItem>
                    <SelectItem value="400000">£400k</SelectItem>
                    <SelectItem value="500000">£500k</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label>Max Price</Label>
                <Select
                  value={filters.max_price}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, max_price: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="200000">£200k</SelectItem>
                    <SelectItem value="250000">£250k</SelectItem>
                    <SelectItem value="300000">£300k</SelectItem>
                    <SelectItem value="350000">£350k</SelectItem>
                    <SelectItem value="400000">£400k</SelectItem>
                    <SelectItem value="500000">£500k</SelectItem>
                    <SelectItem value="750000">£750k</SelectItem>
                    <SelectItem value="1000000">£1M+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label>Bedrooms</Label>
                <Select
                  value={filters.min_bedrooms}
                  onValueChange={(v) => setFilters(prev => ({ ...prev, min_bedrooms: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No properties found</h3>
            <p className="text-slate-500">Try adjusting your search filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card key={property.case_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                {/* Property Image */}
                <div className="h-48 bg-slate-200 relative">
                  {property.main_photo ? (
                    <img
                      src={property.main_photo}
                      alt={property.address_line1}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Home className="w-12 h-12 text-slate-400" />
                    </div>
                  )}
                  <Badge className="absolute top-3 right-3 bg-blue-600">
                    For Sale
                  </Badge>
                </div>
                
                <CardContent className="p-4">
                  {/* Price */}
                  <p className="text-2xl font-bold text-slate-800 mb-1">
                    £{(property.asking_price || property.offer_price || 0).toLocaleString()}
                  </p>
                  
                  {/* Address */}
                  <p className="font-medium text-slate-700 mb-1">
                    {property.address_line1}
                  </p>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mb-3">
                    <MapPin className="w-3 h-3" />
                    {property.postcode}
                  </p>
                  
                  {/* Features */}
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                    {property.bedrooms && (
                      <span className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        {property.bedrooms} bed
                      </span>
                    )}
                    {property.bathrooms && (
                      <span className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        {property.bathrooms} bath
                      </span>
                    )}
                    {property.property_type && (
                      <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {property.property_type}
                      </span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={() => openViewingModal(property)}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Viewing
                    </Button>
                    {property.rightmove_url && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => window.open(property.rightmove_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Viewing Request Modal */}
      <Dialog open={viewingModal} onOpenChange={setViewingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request a Viewing</DialogTitle>
            <DialogDescription>
              {selectedProperty?.address_line1}, {selectedProperty?.postcode}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Preferred Date</Label>
              <Select value={viewingDate} onValueChange={setViewingDate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a date" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableDates().map((date) => (
                    <SelectItem key={date} value={date}>
                      {new Date(date).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short'
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Preferred Time</Label>
              <Select value={viewingTime} onValueChange={setViewingTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any specific requirements or questions..."
                value={viewingNotes}
                onChange={(e) => setViewingNotes(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitViewingRequest} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Request Viewing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertySearch;
